import {
    AuthorizationNotifier,
    AuthorizationRequest,
    AuthorizationServiceConfiguration,
    BaseTokenRequestHandler,
    DefaultCrypto,
    FetchRequestor,
    GRANT_TYPE_AUTHORIZATION_CODE,
    LocalStorageBackend,
    RedirectRequestHandler,
    TokenRequest,
} from '@openid/appauth';
import { getFullJwt } from './getFullJwt';
import { getPartialJwt } from './getPartialJwt';
import { NoHashQueryStringUtils } from './noHashQueryStringUtils';
import { BRAND } from '../AppConstants';

export default class OIDCAuthManager {
    constructor(config) {
        this.config = config;
        this.notifier = new AuthorizationNotifier();
        this.tokenHandler = new BaseTokenRequestHandler(new FetchRequestor());
        this.authorizationHandler = new RedirectRequestHandler(
            new LocalStorageBackend(),
            new NoHashQueryStringUtils(),
            window.location,
            new DefaultCrypto()
        );
        // set notifier to deliver responses
        this.authorizationHandler.setAuthorizationNotifier(this.notifier);
    }

    /**
     * @description Fetches the auth service configuration for interacting with identiy provider
     */
    async getAuthServiceConfiguration() {
        if (!this.authServiceConfiguration) {
            const response = await AuthorizationServiceConfiguration.fetchFromIssuer(
                this.config.authority,
                new FetchRequestor()
            );

            this.authServiceConfiguration = response;
        }
    }

    /**
     * @description Performs step up authentication to the identity provider.
     * @param {string} locale
     * @param {string} returnPath
     * @param {string} clientNumber
     * @param {string} certificate
     * @param {string} group
     * @param {boolean} isOtip
     * @param {string} entityId
     */
    async performStepUpAuthentication(locale, returnPath, clientNumber, certificate, group, isOtip, entityId) {
        // For OTIP users, add the brand to set OTP page with OTIP theme
        const extras = {
            ...(isOtip && { brand: BRAND.OTIP }),
            ui_locales: locale,
            claims: JSON.stringify({
                id_token: { acr: { essential: true, values: ['stepup_conditional'] }, ui_locales: { value: locale } },
            }),
        };

        // add returnPath, certificate, client number, group, and entityId in the OIDC state
        const stateParams = {
            returnPath,
            clientNumber,
            certificate,
            group,
            entityId,
            UUID: crypto.randomUUID(),
        };

        let encodedStateParams = null;
        try {
            encodedStateParams = btoa(JSON.stringify(stateParams));
        } catch (e) {
            throw new Error('Error encoding oidc state');
        }

        const scope = entityId ? this.config.sso_scope : this.config.scope;
        const state = `gb_${encodedStateParams}`;
        // Create a request to step up at identity provider
        const authRequest = new AuthorizationRequest({
            client_id: this.config.client_id,
            redirect_uri: this.config.redirect_uri,
            response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
            scope,
            state,
            extras,
        });

        try {
            await this.getAuthServiceConfiguration();

            // Redirect to the identity provider login using the provided configuration
            this.authorizationHandler.performAuthorizationRequest(this.authServiceConfiguration, authRequest);
        } catch (err) {
            throw new Error('Ooops! Something went wrong when performing auth request');
        }
    }

    /**
     * @description Handles the callback from the identity provider after step up authentication.
     * @param {function} responseHandler
     * @param {string} transactionId
     */
    async stepUpCallback(responseHandler, transactionId) {
        this.notifier.setAuthorizationListener(this.createAuthorizationListener(responseHandler, transactionId));

        // Start the processing of identity provider response (callback)
        return this.authorizationHandler.completeAuthorizationRequestIfPossible();
    }

    /**
     * @description This listener is created to:
        - listen for authorization responses
        - make id, refresh, and access token requests (using auth response)
        - calls responseHandler callback function which handles the redirect after successful step up
     * @param {function} responseHandler 
     * @param {string} transactionId 
     */
    createAuthorizationListener(responseHandler, transactionId) {
        return async (request, response, error) => {
            if (response && !error) {
                try {
                    // Get code verifier (PKCE) from authorization request to enable validation of auth code
                    const codeVerifier = request?.internal?.code_verifier;

                    // Request for tokens: id token, access token, and refresh token - using the auth code
                    const tokenResponse = await this.makeTokenRequest(response.code, codeVerifier);

                    // Decode the OIDC state
                    // The return path is used to redirect the user after successful step up
                    // The client number, certificate, group are used to get the full JWT for authentication
                    const decodedState = atob(response.state.split('gb_')[1]);
                    const stateParams = JSON.parse(decodedState);
                    const returnPath = stateParams['returnPath'];
                    const clientNumber = stateParams['clientNumber'];
                    const certificate = stateParams['certificate'];
                    const group = stateParams['group'];
                    const entityId = stateParams['entityId'];
                    const partialJWT = await getPartialJwt(tokenResponse.idToken, entityId, transactionId);
                    const fullJWT = await getFullJwt(
                        partialJWT,
                        clientNumber,
                        certificate,
                        group,
                        entityId,
                        transactionId
                    );
                    const jwt = fullJWT['gb-authorization'];
                    const sessionCorrelationId = fullJWT['mli-session-correlation-id'];

                    // Call response handler. This function updates redux state and redirects to return path.
                    return responseHandler(jwt, sessionCorrelationId, returnPath);
                } catch (err) {
                    // Error making token request from Auth Service or BFF
                    return responseHandler();
                }
            }

            // If there is an error value or there is no response
            return responseHandler();
        };
    }

    async makeTokenRequest(code, codeVerifier) {
        const extras = { code_verifier: codeVerifier };

        // Create a request for tokens (access token, id token, refresh token)
        const tokenRequest = new TokenRequest({
            client_id: this.config.client_id,
            redirect_uri: this.config.redirect_uri,
            grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
            refresh_token: null,
            code,
            extras,
        });

        await this.getAuthServiceConfiguration();

        // Request for tokens (id token, access token, and refresh token) using the provided configuration
        const tokenResponse = await this.tokenHandler.performTokenRequest(this.authServiceConfiguration, tokenRequest);

        return tokenResponse;
    }
}
